<main id="main-content" class="h-screen flex flex-col pl-8 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="flex items-center gap-2">
                <h1 class="oswald-font text-3xl">
                    {{ currentCustomPage?.title }}
                </h1>
                <div data-popover-target="popover-right" data-popover-placement="right" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-right" role="tooltip" class="absolute z-10 invisible inline-block w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Once a page has been created, it will appear under the Custom Pages section. To edit the custom page’s information, click on the page name. Two functionalities are provided here: 
                        </p>                        
                        <ul class="list-disc ps-5 text-start w-full">
                            <li>Textbox for typing in information</li>
                            <li>File upload (only one file allowed per section)</li>
                        </ul>
                        <p>
                            To update this section, simply fill out the textbox and/or upload a file. To upload a file, click on 
                            <span class="inline-flex px-2 h-8 rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 hover:from-nside-orange-100 hover:to-nside-orange-200 text-white oswald-font flex items-center justify-center gap-2">
                                <lucide-icon [img]="icons.Paperclip" [size]="18"></lucide-icon>
                                <p>Upload files</p>
                            </span>. Click or drag and drop the files that you would like to update. <strong>Note**</strong> if you upload a file when another file has already been uploaded, it will replace the previous file. Ensure that you keep backups of all files in the <strong>Emergency Operations Plan</strong> by adding them to your <strong>Documents</strong> section.

                        </p>
                        <p>
                            Uploaded files can also be downloaded from the <strong>Emergency Operations Plan</strong> by clicking 
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <button 
                *ngIf="canSave()"
                class="w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center hover:text-white hover:bg-nside-gray-200"
                (click)="$event.preventDefault(); handleOpenModal('edit-custom-page');"
            >
                <lucide-icon [img]="icons.Pencil" size="20"></lucide-icon>
            </button>
        </div>
    </div>

    <!-- MAIN CONTENT -->
    <div class="p-2 w-full h-full flex flex-grow flex-col items-start gap-8 overflow-y-auto">
        <div class="w-full flex-1 min-h-1/2 bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-2 overflow-y-auto">
            <form class="w-full h-full flex flex-col gap-2" (ngSubmit)="onSubmit()">
                <div class="w-full h-full whitespace-pre-line break-words" *ngIf="!canSave()">{{ content || 'Empty' }}</div>
                <div class="p-1 w-full h-full flex flex-col gap-2">
                    <textarea noEmoji 
                        *ngIf="canSave()"
                        name="content" [(ngModel)]="content"
                        class="w-full h-full bg-gray-50 p-2 text-sm text-gray-900 rounded-md border border-gray-300 resize-none"
                        placeholder="Type here..."></textarea>
                </div>

                <div class="w-full flex items-end justify-end">
                    <div *ngIf="currentCustomPage?.last_updated_at" class="w-full flex gap-1 items-start justify-start">
                        <p class="oswald-font font-normal">
                            Last Updated At: <span class="raleway-font">{{ currentCustomPage?.last_updated_at | date: 'MM/dd/yyyy - HH:mm' }}</span>
                        </p>
                        <p class="oswald-font font-normal">
                            By: <span class="raleway-font">{{ currentCustomPage?.last_updated_user_name || 'None' }}</span>
                        </p>
                    </div>
                    <button
                        *ngIf="canSave()"
                        type="submit"
                        class="w-20 flex items-center justify-center text-white oswald-font px-4 py-1.5 bg-gradient-to-b rounded-md"
                        [ngClass]="{
                            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !content.trim() || isContentUnchanged,
                            'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': content.trim() && !isContentUnchanged
                        }"
                        [disabled]="!content.trim() || isContentUnchanged"
                    >
                        Save
                    </button>

                </div>
            </form>

        </div>
        <div class="w-full max-h-1/2 bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-2 overflow-y-auto">
            <div class="w-full h-8 flex items-center justify-between gap-2">
                <p class="font-semibold text-lg">Files</p>
                <button
                    *ngIf="canSave()"
                    class="min-w-fit px-2 h-full rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 hover:from-nside-orange-100 hover:to-nside-orange-200 text-white oswald-font flex items-center justify-center gap-2"
                    (click)="$event.preventDefault(); handleOpenModal('custom-file-upload');">
                    <lucide-icon [img]="icons.Paperclip" [size]="18"></lucide-icon>
                    <p>Upload files</p>
                </button>
            </div>
            <ng-container *ngIf="(currentCustomPage?.files?.length ?? 0) > 0; else emptyFile">
                <div class="w-full h-full flex flex-col items-center justify-center gap-2 overflow-y-auto">
                    <div 
                        *ngFor="let file of currentCustomPage?.files || []"
                        class="w-full flex items-center justify-between px-4 py-2 rounded-md border shadow bg-white"
                    >
                        <div class="flex items-center justify-center gap-1">
                            <lucide-icon [img]="icons.FileText" class="w-5 h-5 text-nside-orange-400"></lucide-icon>
                            <button
                                (click)="$event.preventDefault(); downloadFile(file);"
                                class="text-sm font-bold hover:text-nside-orange-400"
                            >
                                {{ file.file_name }}
                            </button>
                        </div>
                        <div class="flex justify-end gap-3">
                            <button 
                                *ngIf="canSave()"
                                class="oswald-font text-red-500 hover:text-red-700 cursor-pointer"
                                (click)="$event.preventDefault(); handleOpenModal('delete-confirmation', file);"
                            >
                                Delete
                            </button>
                            <span class="w-20 text-gray-400">
                                {{ file.uploaded_at | date: 'MM/dd/yyyy' }}
                            </span>
                            <span class="w-16 text-gray-400 whitespace-nowrap">
                                {{ formatFileSize(file.size) }}
                            </span>
                            <button 
                                class="oswald-font text-blue-500 hover:text-nside-orange-400 cursor-pointer"
                                (click)="$event.preventDefault(); downloadFile(file);"
                            >
                                Download
                            </button>
                        </div>
                    </div>
                </div>
            </ng-container>
            
            <ng-template #emptyFile>
                <p class="w-full text-gray-500">No files uploaded yet.</p>
            </ng-template>
              
        </div>
    </div>

    <prv-next-buttons [totalPages]="(eopNavigationService.totalPages$ | async) || []" />

</main>

<introduction></introduction>

<edit-custom-page
    *ngIf="showEditCustomPage"
    [customPage]="currentCustomPage"
    (onClose)="handleCloseModal('edit-custom-page')"
    (onSave)="handleSave($event, 'edit-custom-page')"
></edit-custom-page>

<custom-file-upload
    *ngIf="showCustomFileUpload"
    [pageId]="currentCustomPageId"
    (onClose)="handleCloseModal('custom-file-upload')"
    (onSave)="handleSave($event, 'custom-file-upload')"
></custom-file-upload>

<delete-confirmation
    *ngIf="showDeleteConfirmation"
    [text]="'this file'"
    [itemToDelete]="selectedFile"
    (onClose)="handleCloseModal('delete-confirmation')"
    (onDelete)="deleteFile($event)"
></delete-confirmation>

<popup-message 
    [type]="messageModal"
    [message]="popupMessage"
    (close)="resetMessageModal()"
></popup-message>

<div *ngIf="loading">
    <spinner></spinner>
</div>